import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Popover,
  TextField,
  Tooltip,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createPrefColumn,
  IColumnVisibilityModel,
  IPref,
  IColumn,
} from "../../api/concierges/preferences";
import { IPreferences } from "./interface";
import {
  usePreferences,
  useSetPreferences,
} from "../../reactQuery/preferences/preferences";
import { useSelector } from "react-redux";
import { getAuth } from "../../redux/auth/authSlice";
import { useSnackbarContext } from "../globalSnackNotify/globalSnackNotifyContext";
import { DEFAULT_VIEW_NAME } from "../../utils/constants";
import {
  defaultPinned,
  sanitizePinned,
  pinnedSessionKey,
  selectedViewSessionKey,
} from "../../utils/pinning";
import { GridPinnedColumnFields } from "@mui/x-data-grid/hooks/features/columns/gridColumnsInterfaces";
import { useDivisionFeatureFlags } from "../../stateContext/featureFlags/FeatureFlagProvider";

const Preferences = ({
  page,
  email,
  columns,
  orderedColumns,
  columnReordered,
  columnVisibilityModel,
  setColumnVisibilityModel,
  setOrderedColumns,
  defaultNonVisibleColumns = [],
  selectedViewFromBuyerListing,
  setSelectedViewFromBuyerListing,
  pinnedColumns,
  setPinnedColumns,
}: IPreferences) => {
  const ERROR = "error";
  const SUCCESS = "success";
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isSaveAs, setIsSaveAs] = useState(false);
  const [viewName, setViewName] = useState("");
  const [savedViews, setSavedViews] = useState<string[]>([]);
  const [selectedView, setSelectedView] = useState<string>(selectedViewFromBuyerListing || DEFAULT_VIEW_NAME);
  const [editingViewIndex, setEditingViewIndex] = useState<number | null>(null);
  const [allPreferences, setAllPreferences] = useState<{ [x: string]: IPref }>(
    {}
  );
  // eslint-disable-next-line
  const [viewToDelete, setViewToDelete] = useState<string | null>(null);
  const MAX_CHARACTER_LIMIT = 25;
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [validOperationDialogOpen, setValidOperationDialogOpen] =
    useState(false);
  const [originalViewState, setOriginalViewState] = useState<{
    visibility: IColumnVisibilityModel;
    order: string[];
    pinned: GridPinnedColumnFields;
  } | null>(null);
  const { userInfo } = useSelector(getAuth);
  const { showMilestoneProgress } = useDivisionFeatureFlags(userInfo?.userDivCode || "");
  const { mutate: setPreferences } = useSetPreferences(
    email,
    page,
    userInfo.userDivCode
  );
  const { data: preferences } = usePreferences(
    email,
    page,
    userInfo.userDivCode
  );
  const { showSnackbar } = useSnackbarContext();

  /* Handles the opening of the pop-over component */
  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /* Handles the closing of the pop-over component */
  const handleClose = () => {
    setAnchorEl(null);
    setIsSaveAs(false);
    setViewName("");
    setEditingViewIndex(null);
  };

  /* Handles the double clicking of the view to enable editing capability */
  const handleDoubleClickView = (event: React.MouseEvent, index: number) => {
    event.stopPropagation();
    event.preventDefault();
    setEditingViewIndex(index);
    setViewName(savedViews[index]);
  };

  /* Set the view to delete */
  /* Open the confirmation dialog */
  const handleDeleteIconClick = (viewName: string) => {
    setViewToDelete(viewName);
    setIsDeleteDialogOpen(true);
  };

  /*
   * This function handles the confirmation of a view deletion.
   * It checks if there is a view selected to delete, finds its index in the saved views,
   * calls the delete function if the view is found, closes the delete dialog, and resets the viewToDelete state.
   */
  const handleConfirmDelete = async () => {
    if (!viewToDelete) return;

    const index = savedViews.indexOf(viewToDelete);
    if (index > -1) {
      await handleDeleteView(index);
    }
    setIsDeleteDialogOpen(false);
    setViewToDelete(null);
  };

  /* Handles the canceling of the delete operation */
  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setViewToDelete(null);
  };

  /* This function handles the canceling of the 'Save As' operation. */
  const handleCancelSaveAs = () => {
    setIsSaveAs(false);
    setViewName("");
    setEditingViewIndex(null);
  };

  /* This function handles the 'Save As' click to create a new view. */
  const handleSaveAsClick = () => {
    setIsSaveAs(true);
  };

  const handleInvalidDialogClose = () => {
    setValidOperationDialogOpen(false);
  };

  /*
   * This function handles the deletion of a view from the saved views list and preferences.
   * It removes the view from both local state and the preferences object,
   * updates the selected view if the deleted view was currently active, and sends a PUT request to save the changes.
   */
  const handleDeleteView = async (index: number) => {
    const viewToDelete = savedViews[index];
    const updatedViews = savedViews.filter((_, i) => i !== index);

    const updatedPreferences = {
      ...allPreferences,
    }; /* Remove the deleted view from the preferences object */

    delete updatedPreferences[
      viewToDelete
    ]; /* This line removes the deleted view */

    setSavedViews(updatedViews); /* Update local state for views */
    setAllPreferences(
      updatedPreferences
    ); /* Update local state for preferences */

    try {
      setPreferences(updatedPreferences, {
        onSuccess: () => {
          showSnackbar(`View '${viewToDelete}' Deleted Successfully!`, SUCCESS);
        },
        onError: (error) => {
          showSnackbar("Error deleting view", ERROR);
          console.error("Error during delete view:", error);
        },
      });
    } catch (error) {
      showSnackbar("Error deleting view", ERROR);
      console.error("Error during delete view:", error);
    }
  };

  /*
   * This function handles saving the view name, either by creating a new view ('Save As') or renaming an existing view.
   * It validates the view name, checks if it already exists, and sends a PUT request to save the view to the server.
   */
  const handleSaveViewName = async () => {
    if (isSaveAs) {
      /* Save As logic (creating a new view) */
      if (!viewName.trim()) {
        setValidOperationDialogOpen(true);
        return;
      }

      const newViewName = viewName.trim();

      /* Check if the new view name already exists or is "Default View"*/
      if (
        savedViews.includes(newViewName) ||
        newViewName === DEFAULT_VIEW_NAME
      ) {
        setValidOperationDialogOpen(true);
        return;
      }

      const columnMappingValue =
        orderedColumns.length > 0
          ? orderedColumns.map((field) =>
              columns.find((col) => col.field === field)
            )
          : columns;

      const validColumns = columnMappingValue.filter(Boolean);

      try {
        /* Create a new preferences object for the new view */
        const newPreferences: { [x: string]: IPref } = {
          ...allPreferences,
          [newViewName]: {
            preferences: validColumns.map((col) =>
              createPrefColumn(
                col?.field,
                col?.field ? columnVisibilityModel[col.field] ?? true : true,
                col?.field ? columnVisibilityModel[col.field] ?? true : true
              )
            ),
            pinned: sanitizePinned(pinnedColumns ?? defaultPinned(showMilestoneProgress), showMilestoneProgress), // persist pinning for this new custom view
          },
        };

        setPreferences(newPreferences, {
          onSuccess: () => {
            /* Update the local state to reflect the new view */
            /* Add the new view to saved views */
            setSavedViews([...savedViews, newViewName]);
            /* Update all preferences */
            setAllPreferences(newPreferences);
            /* Set the newly saved view as the selected view */
            setSelectedView(newViewName);
            setSelectedViewFromBuyerListing?.(newViewName);
            sessionStorage.setItem(selectedViewSessionKey(page), newViewName);
            showSnackbar(`View '${newViewName}' Saved Successfully!`, SUCCESS);
          },
          onError: (error) => {
            showSnackbar("Failed to Save the New View.", ERROR);
            console.error("Error saving the New View:", error);
          },
        });
      } catch (error) {
        console.error("Error saving the new view", error);
        showSnackbar("Failed to Save the New View.", ERROR);
      }

      setIsSaveAs(false);
      setViewName("");
      setEditingViewIndex(null);
    } else {
      if (editingViewIndex === null || !viewName.trim()) {
        setValidOperationDialogOpen(true);
        return;
      }

      const oldViewName = savedViews[editingViewIndex];
      const newViewName = viewName.trim();

      if (
        newViewName === oldViewName ||
        savedViews.includes(newViewName) ||
        newViewName === DEFAULT_VIEW_NAME
      ) {
        setValidOperationDialogOpen(true);
        return;
      }

      try {
        const oldViewPreferences = allPreferences[oldViewName];

        /* If the old view preferences are missing, throwing an alert to avoid overwriting */
        if (!oldViewPreferences) {
          showSnackbar("Old view preferences are missing.", ERROR);
          return;
        }

        const updatedPreferences: { [x: string]: IPref } = {
          ...allPreferences,
          [newViewName]:
            oldViewPreferences /* Keep the old view's preferences under the new name */,
        };
        /* Remove the old view name */
        delete updatedPreferences[oldViewName];

        setPreferences(updatedPreferences, {
          onSuccess: () => {
            /* Update the local state to reflect the new view name */
            const updatedViews = [...savedViews];
            /* Update the view name in the list */
            updatedViews[editingViewIndex] = newViewName;
            setSavedViews(updatedViews);
            /* Update the preferences in the state */
            setAllPreferences(updatedPreferences);

            if (oldViewName === selectedView) {
              setSelectedView(newViewName);
            }
            showSnackbar(
              `View '${oldViewName}' Renamed to '${newViewName}' Successfully!`,
              SUCCESS
            );
          },
          onError: (error) => {
            showSnackbar("Failed to rename the view.", ERROR);
            console.error("Error renaming the view:", error);
          },
        });
      } catch (error) {
        showSnackbar("Failed to rename the view.", ERROR);
      }
      setEditingViewIndex(null);
      setViewName("");
    }
  };

  const pinnedColumnsRef = useRef(pinnedColumns);
  useEffect(() => {
    pinnedColumnsRef.current = pinnedColumns;
  }, [pinnedColumns]);

  /*
   * This function saves the current view's column visibility settings.
   * It ensures that all columns (both visible and hidden) are included in the updated preferences,
   * and then sends a PUT request to update the current view's preferences on the server.
   * If the update is successful, it updates the local state to reflect the changes for the selected view.
   */
  const handleSaveView = useCallback(async () => {
    if (!selectedView) {
      showSnackbar("No View is Selected to Save.", ERROR);
      return;
    }

    const columnMappingValue =
      orderedColumns.length > 0
        ? orderedColumns.map((field) =>
            columns.find((col) => col.field === field)
          )
        : columns;

    const validColumns = columnMappingValue.filter(Boolean);

    const updatedViewPreferences = validColumns.map((col) => {
      const columnField = col?.field;
      return {
        columnName: columnField,
        changeVisibilityFlag: columnField
          ? columnVisibilityModel?.[columnField] ?? true
          : true,
        visibilityFlag: columnField
          ? columnVisibilityModel?.[columnField] ?? true
          : true,
      };
    });

    try {
      const currentPinned = sanitizePinned(pinnedColumnsRef.current ?? defaultPinned(showMilestoneProgress), showMilestoneProgress);
      
      const updatedPreferences = {
        ...allPreferences /* Keep the other views intact */,
        [selectedView]: {
          preferences: updatedViewPreferences,
          pinned: currentPinned,
        },
      };

      setPreferences(updatedPreferences, {
        onSuccess: () => {
          /* Update the local state to reflect the changes in the current view */
          setAllPreferences(updatedPreferences);
          showSnackbar(`View '${selectedView}' Updated Successfully!`, SUCCESS);
          
          setOriginalViewState({
            visibility: { ...columnVisibilityModel },
            order: [...orderedColumns],
            pinned: JSON.parse(JSON.stringify(currentPinned)),
          });
        },
        onError: () => {
          showSnackbar(`Failed to Update the View '${selectedView}'.`, ERROR);
        },
      });
    } catch (error) {
      showSnackbar(`Failed to Update the View '${selectedView}'.`, ERROR);
    }
    // eslint-disable-next-line
  }, [
    selectedView,
    columnVisibilityModel,
    email,
    allPreferences,
    columns,
    orderedColumns,
    showSnackbar,
    SUCCESS,
    ERROR,
    setPreferences,
    showMilestoneProgress,
  ]);

  /*
   * This function applies the column visibility preferences for a specific view.
   * If a valid view preference is provided, it updates the column visibility model.
   * If the view preference is invalid, it displays an alert.
   */
  const applyViewPreferences = useCallback(
    (viewPreference: IColumnVisibilityModel) => {
      if (viewPreference) {
        setColumnVisibilityModel(viewPreference);
      } else {
        showSnackbar("Invalid View Preference Provided", ERROR);
      }
    },
    // eslint-disable-next-line
    []
  );

  /*
   *This function creates visibility model for the default Columns.
   *It sets the visibility of columns based on the defaultNonVisibleColumns array.
   */
  const createDefaultVisibilityModel = useCallback(() => {
    return columns.reduce((acc: IColumnVisibilityModel, col) => {
      acc[col.field] = !defaultNonVisibleColumns.includes(col.field);
      return acc;
    }, {});
  }, [columns, defaultNonVisibleColumns]);

  /*
   * This function handles selecting a view from the list of saved views.
   * It updates the selected view state and applies the column visibility preferences for that view.
   * If the selected view has preferences, it constructs the visibility model and applies it;
   * otherwise, it applies an empty visibility model.
   */
  const handleSelectView = useCallback(
    (name: string) => {
      setSelectedView(name);
      sessionStorage.setItem(selectedViewSessionKey(page), name);
      setSelectedViewFromBuyerListing?.(name);
      let visibilityModel: IColumnVisibilityModel = {};
      let cols = columns.map((col) => col.field);
      if (name === DEFAULT_VIEW_NAME) {
        /* Apply the default visibility model (all columns visible) */
        visibilityModel = createDefaultVisibilityModel();
      } else if (allPreferences[name] && allPreferences[name].preferences) {
        const selectedPreferences = allPreferences[name].preferences;
        selectedPreferences.forEach((preference: any) => {
          visibilityModel[preference.columnName] = preference.visibilityFlag;
        });

        /* Set column order based on the selected view's preferences */
        cols = selectedPreferences.map(
          (preference: any) => preference.columnName
        );
      }
      applyViewPreferences(visibilityModel);
      setOrderedColumns(cols);

      // Default View: defaultPinned() only; Custom View: use its saved pinning or fallback to default
      const pinnedForView =
        name === DEFAULT_VIEW_NAME
          ? defaultPinned(showMilestoneProgress)
          : sanitizePinned(allPreferences[name]?.pinned ?? defaultPinned(showMilestoneProgress), showMilestoneProgress);

      setPinnedColumns?.(pinnedForView);
      // keep tab-session behavior so navigating away/back restores pinning
      const key = pinnedSessionKey(page, name);
      sessionStorage.setItem(key, JSON.stringify(pinnedForView));

      // capture original state after applying the view
      setOriginalViewState({
        visibility: { ...visibilityModel },
        order: [...cols],
        pinned: JSON.parse(JSON.stringify(pinnedForView)), // deep clone
      });
    },
    [
      allPreferences,
      applyViewPreferences,
      columns,
      setOrderedColumns,
      setPinnedColumns,
      page,
      setSelectedViewFromBuyerListing,
      createDefaultVisibilityModel,
      showMilestoneProgress,
    ]
  );

  const existingColumnsSetMemoized = useMemo(() => {
    if (!preferences || Object.keys(preferences).length === 0) {
      return new Set();
    }
    const viewNames = Object.keys(preferences);
    if (viewNames.length === 0) return new Set<string>();

    /**
     * Gather column names from each view's preferences
     */
    const viewColumns = viewNames.map(
      (viewName) =>
        new Set(
          preferences[viewName]?.preferences.map((p) => p.columnName) || []
        )
    );

    return viewColumns.reduce((commonColumnsSet: any, currentViewColumns) => {
      if (commonColumnsSet.size === 0) return new Set(currentViewColumns);

      return new Set(
        [...commonColumnsSet].filter((c) => currentViewColumns.has(c))
      );
    }, new Set());
  }, [preferences]);

  const newColumnsMemoized = useMemo(() => {
    if (!preferences) return [];
    if (Object.keys(preferences).length === 0) return [];
    return columns.filter((col) => !existingColumnsSetMemoized.has(col.field));
  }, [preferences, columns, existingColumnsSetMemoized]);

  /* Create a new object to accumulate updated preferences
  Keeping the name as updatedPrefs as they're originally called the same in useEffect.*/
  const updatedPrefsMemoized = useMemo(() => {
    if (!preferences) return null;
    if (!newColumnsMemoized.length) return null;

    const viewNames = Object.keys(preferences);
    if (!viewNames.length) return null;

    let changed = false;
    const result: Record<string, IPref> = {};

    viewNames.forEach((viewName) => {
      const original = preferences[viewName];
      const list = [...(original?.preferences ?? [])];
      const actionsIndex = list.findIndex((p) => p.columnName === "actions");

      newColumnsMemoized.forEach((col) => {
        const columnExistsInView = list.some((p) => p.columnName === col.field);
        if (!columnExistsInView && !existingColumnsSetMemoized.has(col.field)) {
          changed = true;
          const newColumnPref = {
            columnName: col.field,
            visibilityFlag: false,
            changeVisibilityFlag: false,
          };
          if (actionsIndex !== -1) {
            list.splice(actionsIndex, 0, newColumnPref);
          } else {
            list.push(newColumnPref);
          }
        }
      });

      result[viewName] = {
        ...(original as IPref),
        preferences: list,
        pinned: (original as IPref)?.pinned,
      };
    });

    return changed ? result : null;
  }, [preferences, newColumnsMemoized, existingColumnsSetMemoized]);

  /**
   * Detects if the current view has any unsaved changes
   * Compares: column visibility, column order, and pinned columns
   */
  const hasViewChanges = useCallback((): boolean => {
    if (!originalViewState || selectedView === DEFAULT_VIEW_NAME) {
      return false; // Default view can't be saved
    }

    // Check visibility changes
    const visibilityChanged = 
      Object.keys(columnVisibilityModel).some(
        (field) => columnVisibilityModel[field] !== originalViewState.visibility[field]
      ) ||
      Object.keys(originalViewState.visibility).some(
        (field) => !(field in columnVisibilityModel) || columnVisibilityModel[field] !== originalViewState.visibility[field]
      );

    // Check order changes
    const orderChanged =
      orderedColumns.length !== originalViewState.order.length ||
      orderedColumns.some((field, idx) => field !== originalViewState.order[idx]);

    // Check pinned changes
    const pinnedChanged =
      JSON.stringify(pinnedColumnsRef.current) !== JSON.stringify(originalViewState.pinned);

    return visibilityChanged || orderChanged || pinnedChanged;
  }, [originalViewState, selectedView, columnVisibilityModel, orderedColumns]);

  /*
   * This useEffect hook fetches user preferences based on the user's email and sets the column visibility model accordingly.
   * If user preferences are not available, it applies a default visibility model where all columns are visible.
   */
  useEffect(() => {
    if (!email) return;

    // Initialize basic view lists when preferences load
    if (preferences && Object.keys(preferences).length > 0) {
      setAllPreferences(preferences);
      setSavedViews(Object.keys(preferences));
       if (!selectedViewFromBuyerListing) {
           setSelectedView(DEFAULT_VIEW_NAME);
         }
    }

    if (updatedPrefsMemoized) {
      setPreferences(updatedPrefsMemoized);
    }

    // Build initial visibility model- setInitialVisibilityModel previously.
    const visibilityModel: IColumnVisibilityModel =
      updatedPrefsMemoized && Object.keys(updatedPrefsMemoized).length > 0
        ? Object.keys(updatedPrefsMemoized).reduce(
            (acc: IColumnVisibilityModel, viewName) => {
              updatedPrefsMemoized[viewName].preferences.forEach((p: any) => {
                acc[p.columnName] = p.changeVisibilityFlag;
              });
              return acc;
            },
            {}
          )
        : createDefaultVisibilityModel();

    setColumnVisibilityModel(visibilityModel);

    // eslint-disable-next-line
  }, [email, columns, setColumnVisibilityModel, page, preferences]);

  useEffect(() => {
    if (selectedView === undefined && allPreferences === undefined) {
      const initialVisibilityModel: IColumnVisibilityModel = {};
      columns.forEach((col) => {
        initialVisibilityModel[col.field] = true;
      });
      setColumnVisibilityModel(initialVisibilityModel);
    }

    if (selectedView === DEFAULT_VIEW_NAME) {
      const visibilityModel = createDefaultVisibilityModel();
      applyViewPreferences(visibilityModel);
      // Set original state for Default View
      setOriginalViewState({
        visibility: { ...visibilityModel },
        order: [...columns.map((col) => col.field)],
        pinned: JSON.parse(JSON.stringify(defaultPinned(showMilestoneProgress))),
      });
    } else if (selectedView && allPreferences[selectedView]) {
      const selectedPreferences =
        allPreferences[selectedView]?.preferences || [];
      const visibilityModel: IColumnVisibilityModel = {};

      selectedPreferences.forEach((preference: any) => {
        visibilityModel[preference.columnName] = preference.visibilityFlag;
      });
      applyViewPreferences(visibilityModel);

      const reorderedColumns = selectedPreferences.map(
        (preference: any) => preference.columnName
      );
      setOrderedColumns(reorderedColumns);

      // Set original state for custom views
      const pinnedForView = sanitizePinned(
        allPreferences[selectedView]?.pinned ?? defaultPinned(showMilestoneProgress),
        showMilestoneProgress
      );
      setOriginalViewState({
        visibility: { ...visibilityModel },
        order: [...reorderedColumns],
        pinned: JSON.parse(JSON.stringify(pinnedForView)),
      });
    }
    // eslint-disable-next-line
  }, [
    selectedView,
    allPreferences,
    applyViewPreferences,
    columns,
    setColumnVisibilityModel,
    showMilestoneProgress,
  ]);

  return (
    <div>
      <Button
        onClick={handleOpen}
        style={{
          display: "flex",
          justifyContent: "space-between",
          textTransform: "none",
          marginLeft: "6px",
          borderBottom: "1px solid #1C2B36",
          width: "265px",
          backgroundColor: "#FAFBFC",
          color: "#1C2B36",
          fontSize: "16px",
        }}
      >
        {selectedView}
        <ArrowDropDownIcon />
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        disableEnforceFocus
        disableAutoFocus
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            padding: "8px",
            minWidth: "260px",
          }}
        >
          <div>
            {[DEFAULT_VIEW_NAME, ...savedViews].map((name, index) =>
              editingViewIndex === index - 1 ? (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <TextField
                    variant="outlined"
                    size="small"
                    value={viewName}
                    onChange={(e) => setViewName(e.target.value)}
                    style={{ marginRight: "8px" }}
                    helperText={`${viewName.length}/${MAX_CHARACTER_LIMIT} characters`}
                    inputProps={{
                      maxLength: MAX_CHARACTER_LIMIT,
                    }}
                  />
                  <span>
                    <Tooltip title="Confirm">
                      <IconButton onClick={handleSaveViewName}>
                        <CheckIcon />
                      </IconButton>
                    </Tooltip>
                  </span>
                  <span>
                    <Tooltip title="Cancel">
                      <IconButton onClick={handleCancelSaveAs}>
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  </span>
                </div>
              ) : (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  {name !== DEFAULT_VIEW_NAME && (
                    <span>
                      <Tooltip title="Delete">
                        <span>
                          <IconButton
                            onClick={() => handleDeleteIconClick(name)}
                            style={{ marginRight: "8px" }}
                            disabled={
                              isSaveAs ||
                              editingViewIndex !== null ||
                              name === selectedView
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </span>
                  )}
                  <MenuItem
                    onClick={() => handleSelectView(name)}
                    onDoubleClick={(event) => {
                      if (name !== DEFAULT_VIEW_NAME) {
                        handleDoubleClickView(event, index - 1);
                      }
                    }}
                    disabled={isSaveAs || editingViewIndex !== null}
                    style={{
                      backgroundColor:
                        name === selectedView ? "#E0F1FE" : "transparent",
                      width: "100%",
                    }}
                  >
                    {name}
                  </MenuItem>
                </div>
              )
            )}

            {isSaveAs && editingViewIndex === null && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginTop: "8px",
                }}
              >
                <TextField
                  label="Enter view name"
                  variant="outlined"
                  size="small"
                  value={viewName}
                  onChange={(e) => setViewName(e.target.value)}
                  style={{ marginRight: "8px" }}
                  helperText={`${viewName.length}/${MAX_CHARACTER_LIMIT} characters`}
                  inputProps={{
                    maxLength: MAX_CHARACTER_LIMIT,
                  }}
                />

                <Tooltip title="Confirm">
                  <IconButton onClick={handleSaveViewName}>
                    <CheckIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton onClick={handleCancelSaveAs}>
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </div>
            )}
          </div>

          <div
            style={{
              marginTop: "auto",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <Button
              variant="text"
              onClick={handleSaveView}
              style={{ textTransform: "none", marginBottom: "4px" }}
              disabled={
                isSaveAs ||
                editingViewIndex !== null ||
                selectedView === DEFAULT_VIEW_NAME ||
                !hasViewChanges() // disable if no changes
              }
            >
              Save
            </Button>
            <Button
              variant="text"
              onClick={handleSaveAsClick}
              style={{ textTransform: "none", marginBottom: "4px" }}
              disabled={
                savedViews.length >= 5 || isSaveAs || editingViewIndex !== null
              }
            >
              Save As
            </Button>
          </div>
        </div>
      </Popover>
      <Dialog open={isDeleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete View</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the view{" "}
            <span style={{ fontWeight: "bold" }}>{viewToDelete}</span>? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelDelete}
            variant="contained"
            style={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            style={{ textTransform: "none", border: "2px solid #005DAA" }}
          >
            Yes, delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={validOperationDialogOpen}
        onClose={handleInvalidDialogClose}
      >
        <DialogTitle>Invalid Operation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The{" "}
            <span style={{ fontWeight: "bold" }}>
              view name cannot already exist or be empty
            </span>
            . Please try again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleInvalidDialogClose}
            variant="outlined"
            style={{ textTransform: "none", border: "2px solid" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Preferences;
